import * as THREE from 'three';
import { rng } from './noise';
import { carPartOf } from '@/entities/carLook';

/**
 * livery/wheels.ts (M144) — PAINTED WHEELS. A tire set is normally two flat
 * colours (rubber + rim); a set with `art` wears a painted wheel FACE instead:
 * one square painting of the whole wheel seen from the side, projected
 * straight down the axle onto the tyre and the rim, with a matching relief.
 *
 * DRAGON CLAWS (owner): "a dragon claw bone look and feel with rims that are
 * the same colour — they should shine." The wheel is a dragon's foot seen
 * from below: five finger bones run out from the hub across the rim to five
 * knuckles, and from each knuckle a polished talon curls round the tyre to
 * the tread. Tyre and rim are the same bone, lacquered.
 *
 * MEASURED in the garage: talons that start at the hub spend their thick half
 * hidden behind the spokes and only their thin tips reach the tyre, which
 * reads as a peppermint swirl. The talons live ON THE TYRE.
 */

type G = CanvasRenderingContext2D;
type Pt = [number, number];

export type WheelArtId = 'claws';

export interface WheelArt {
  map: THREE.CanvasTexture;
  bump: THREE.CanvasTexture;
}

/** The two paintings (colour + relief) of the claw wheel, `size` px square. */
export function clawWheelCanvases(size = 512): { colour: HTMLCanvasElement; relief: HTMLCanvasElement } {
  const mk = (): [HTMLCanvasElement, G] => {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    return [c, c.getContext('2d')!];
  };
  const [colour, g] = mk();
  const [relief, d] = mk();
  const cx = size / 2;
  const R = size * 0.48; // the tyre's outer edge
  const rnd = rng(7331);

  // The bed the talons lie on: old, dark, cracked bone, darker in the grooves.
  const bed = g.createRadialGradient(cx, cx, R * 0.15, cx, cx, R * 1.04);
  bed.addColorStop(0, '#9a8558');
  bed.addColorStop(0.55, '#7b6842');
  bed.addColorStop(0.9, '#5a4a2c');
  bed.addColorStop(1, '#3d311c');
  g.fillStyle = bed;
  g.fillRect(0, 0, size, size);
  d.fillStyle = 'rgb(46,46,46)';
  d.fillRect(0, 0, size, size);
  // Pitting: old bone is porous.
  for (let i = 0; i < 420; i++) {
    const a = rnd() * 6.283;
    const r0 = R * Math.sqrt(rnd());
    const pr = size * (0.002 + rnd() * 0.004);
    g.fillStyle = rnd() < 0.5 ? 'rgba(40,28,10,0.35)' : 'rgba(255,240,200,0.12)';
    g.beginPath();
    g.arc(cx + Math.cos(a) * r0, cx + Math.sin(a) * r0, pr, 0, 6.283);
    g.fill();
  }
  // Fine cracks in the bed.
  g.strokeStyle = 'rgba(30,20,6,0.55)';
  g.lineWidth = Math.max(1, size / 512);
  for (let i = 0; i < 60; i++) {
    const a = rnd() * 6.283;
    const r0 = R * (0.3 + rnd() * 0.65);
    g.beginPath();
    g.moveTo(cx + Math.cos(a) * r0, cx + Math.sin(a) * r0);
    g.lineTo(cx + Math.cos(a + (rnd() - 0.5) * 0.3) * (r0 + R * 0.1 * rnd()), cx + Math.sin(a + (rnd() - 0.5) * 0.3) * (r0 + R * 0.1 * rnd()));
    g.stroke();
  }

  // The talons: on the tyre, from the rim's edge to the tread.
  const N = 5;
  const SWEEP = 1.05; // radians each talon curls round the wheel
  const R0 = 0.5; // where the rim ends and the tyre begins (of R)
  const claw = (theta0: number, widthK: number): Pt[][] => {
    const left: Pt[] = [];
    const right: Pt[] = [];
    const steps = 22;
    const at = (t: number): Pt => {
      const r = R * (R0 + (0.995 - R0) * t);
      const th = theta0 + SWEEP * Math.pow(t, 1.1);
      return [cx + Math.cos(th) * r, cx + Math.sin(th) * r];
    };
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      // A thick root that swells a little, then a long taper to a needle.
      const swell = t < 0.16 ? 0.86 + 0.14 * Math.sin((t / 0.16) * (Math.PI / 2)) : Math.pow(1 - (t - 0.16) / 0.84, 0.82);
      const half = R * 0.15 * widthK * swell;
      const [px, py] = at(t);
      const [qx, qy] = at(t + 0.01);
      const tl = Math.hypot(qx - px, qy - py) || 1;
      const nx = -(qy - py) / tl;
      const ny = (qx - px) / tl;
      left.push([px + nx * half, py + ny * half]);
      right.push([px - nx * half, py - ny * half]);
    }
    return [left, right];
  };
  const trace = (c: G, left: Pt[], right: Pt[]): void => {
    c.beginPath();
    c.moveTo(left[0][0], left[0][1]);
    for (let i = 1; i < left.length; i++) c.lineTo(left[i][0], left[i][1]);
    for (let i = right.length - 1; i >= 0; i--) c.lineTo(right[i][0], right[i][1]);
    c.closePath();
  };
  // The rim: polished bone, a shade lighter than the tyre's bed, so the two
  // read as one material and the wheel still has a centre.
  const rimFill = g.createRadialGradient(cx, cx, R * 0.1, cx, cx, R * R0);
  rimFill.addColorStop(0, '#cdbd8e');
  rimFill.addColorStop(0.7, '#b3a172');
  rimFill.addColorStop(1, '#7e6b43');
  g.fillStyle = rimFill;
  g.beginPath();
  g.arc(cx, cx, R * R0, 0, Math.PI * 2);
  g.fill();
  d.fillStyle = 'rgb(84,84,84)';
  d.beginPath();
  d.arc(cx, cx, R * R0, 0, Math.PI * 2);
  d.fill();
  for (let k = 0; k < N; k++) {
    const th0 = (k / N) * Math.PI * 2;
    // The finger bone: hub to knuckle, waisted like a real phalanx.
    {
      const a: Pt = [cx + Math.cos(th0) * R * 0.2, cx + Math.sin(th0) * R * 0.2];
      const b: Pt = [cx + Math.cos(th0) * R * (R0 - 0.03), cx + Math.sin(th0) * R * (R0 - 0.03)];
      const nx = -Math.sin(th0);
      const ny = Math.cos(th0);
      const bone = (c: G, wk: number): void => {
        const w0 = R * 0.085 * wk;
        const w1 = R * 0.05 * wk;
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2;
        c.beginPath();
        c.moveTo(a[0] + nx * w0, a[1] + ny * w0);
        c.quadraticCurveTo(mx + nx * w1, my + ny * w1, b[0] + nx * w0, b[1] + ny * w0);
        c.lineTo(b[0] - nx * w0, b[1] - ny * w0);
        c.quadraticCurveTo(mx - nx * w1, my - ny * w1, a[0] - nx * w0, a[1] - ny * w0);
        c.closePath();
      };
      g.save();
      g.shadowColor = 'rgba(0,0,0,0.7)';
      g.shadowBlur = size * 0.014;
      bone(g, 1);
      g.fillStyle = '#a8946a';
      g.fill();
      g.restore();
      bone(g, 0.62);
      g.fillStyle = '#e4d6ae';
      g.fill();
      bone(g, 0.24);
      g.fillStyle = '#fffaea';
      g.fill();
      bone(d, 1);
      d.fillStyle = 'rgb(150,150,150)';
      d.fill();
      bone(d, 0.5);
      d.fillStyle = 'rgb(214,214,214)';
      d.fill();
    }
    // Three nested passes: the claw, its lit body, its polished ridge.
    const layers: [number, string, string][] = [
      [1.0, '#a18a5a', 'rgb(128,128,128)'],
      [0.72, '#dccca0', 'rgb(184,184,184)'],
      [0.42, '#f5ecd2', 'rgb(226,226,226)'],
      [0.15, '#fffdf2', 'rgb(252,252,252)'],
    ];
    // Contact shadow first, so each claw sits on the bed.
    {
      const [l, r] = claw(th0, 1.0);
      g.save();
      g.shadowColor = 'rgba(0,0,0,0.9)';
      g.shadowBlur = size * 0.024;
      g.shadowOffsetX = size * 0.006;
      g.shadowOffsetY = size * 0.008;
      trace(g, l, r);
      g.fillStyle = '#3a2d18';
      g.fill();
      g.restore();
    }
    for (const [wk, col, rel] of layers) {
      const [l, r] = claw(th0 + (1 - wk) * 0.03, wk);
      trace(g, l, r);
      g.fillStyle = col;
      g.fill();
      trace(d, l, r);
      d.fillStyle = rel;
      d.fill();
    }
    // The dark flank (the side away from the light) and the growth rings.
    const [l, r] = claw(th0, 1.0);
    g.save();
    trace(g, l, r);
    g.clip();
    g.strokeStyle = 'rgba(70,48,14,0.5)';
    g.lineWidth = size * 0.02;
    g.beginPath();
    r.forEach(([x, y], i) => (i === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
    g.stroke();
    g.strokeStyle = 'rgba(90,66,24,0.26)';
    g.lineWidth = Math.max(1, size * 0.003);
    for (let i = 2; i < l.length - 8; i += 3) {
      g.beginPath();
      g.moveTo(l[i][0], l[i][1]);
      g.quadraticCurveTo((l[i][0] + r[i][0]) / 2 + (l[i + 1][0] - l[i][0]) * 0.6, (l[i][1] + r[i][1]) / 2 + (l[i + 1][1] - l[i][1]) * 0.6, r[i][0], r[i][1]);
      g.stroke();
    }
    g.restore();
    trace(g, l, r);
    g.strokeStyle = 'rgba(30,20,6,0.8)';
    g.lineWidth = Math.max(1, size * 0.005);
    g.stroke();
    // The knuckle the talon grows from: a domed knob on the rim's edge.
    {
      const kx = cx + Math.cos(th0) * R * R0;
      const ky = cx + Math.sin(th0) * R * R0;
      const kr = R * 0.125;
      const knob = g.createRadialGradient(kx - kr * 0.3, ky - kr * 0.35, kr * 0.05, kx, ky, kr);
      knob.addColorStop(0, '#fffdf4');
      knob.addColorStop(0.5, '#e0d1a4');
      knob.addColorStop(1, '#857045');
      g.save();
      g.shadowColor = 'rgba(0,0,0,0.75)';
      g.shadowBlur = size * 0.016;
      g.fillStyle = knob;
      g.beginPath();
      g.arc(kx, ky, kr, 0, Math.PI * 2);
      g.fill();
      g.restore();
      g.strokeStyle = 'rgba(40,28,8,0.7)';
      g.lineWidth = Math.max(1, size * 0.004);
      g.beginPath();
      g.arc(kx, ky, kr, 0, Math.PI * 2);
      g.stroke();
      const kd = d.createRadialGradient(kx, ky, 0, kx, ky, kr);
      kd.addColorStop(0, 'rgb(250,250,250)');
      kd.addColorStop(1, 'rgb(150,150,150)');
      d.fillStyle = kd;
      d.beginPath();
      d.arc(kx, ky, kr, 0, Math.PI * 2);
      d.fill();
    }
  }

  // The hub: a domed knuckle of bone with a dark socket.
  const hub = g.createRadialGradient(cx - R * 0.06, cx - R * 0.07, R * 0.01, cx, cx, R * 0.27);
  hub.addColorStop(0, '#fffdf4');
  hub.addColorStop(0.45, '#e2d4a8');
  hub.addColorStop(1, '#8a7446');
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.8)';
  g.shadowBlur = size * 0.02;
  g.fillStyle = hub;
  g.beginPath();
  g.arc(cx, cx, R * 0.25, 0, Math.PI * 2);
  g.fill();
  g.restore();
  g.strokeStyle = 'rgba(40,28,8,0.7)';
  g.lineWidth = Math.max(1, size * 0.005);
  g.beginPath();
  g.arc(cx, cx, R * 0.25, 0, Math.PI * 2);
  g.stroke();
  const sock = g.createRadialGradient(cx, cx, 0, cx, cx, R * 0.085);
  sock.addColorStop(0, '#0b0804');
  sock.addColorStop(1, '#3d2f18');
  g.fillStyle = sock;
  g.beginPath();
  g.arc(cx, cx, R * 0.085, 0, Math.PI * 2);
  g.fill();
  const hd = d.createRadialGradient(cx, cx, 0, cx, cx, R * 0.25);
  hd.addColorStop(0, 'rgb(90,90,90)');
  hd.addColorStop(0.33, 'rgb(90,90,90)');
  hd.addColorStop(0.36, 'rgb(245,245,245)');
  hd.addColorStop(1, 'rgb(140,140,140)');
  d.fillStyle = hd;
  d.beginPath();
  d.arc(cx, cx, R * 0.25, 0, Math.PI * 2);
  d.fill();
  return { colour, relief };
}

/** The painted wheel as shop card art: the wheel face, floating. */
export function wheelCardCanvas(w = 360, hgt = 240): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = hgt;
  const g = c.getContext('2d')!;
  const d = Math.min(w, hgt) * 0.9;
  const face = clawWheelCanvases(Math.round(d * 1.04)).colour;
  g.save();
  g.shadowColor = 'rgba(0,0,0,0.65)';
  g.shadowBlur = d * 0.09;
  g.shadowOffsetY = d * 0.045;
  g.beginPath();
  g.arc(w / 2, hgt / 2, d * 0.48, 0, Math.PI * 2);
  g.fillStyle = '#1d160d';
  g.fill();
  g.restore();
  g.save();
  g.beginPath();
  g.arc(w / 2, hgt / 2, d * 0.48, 0, Math.PI * 2);
  g.clip();
  g.drawImage(face, w / 2 - face.width / 2, hgt / 2 - face.height / 2);
  // A lacquer sheen across the top: it should look polished.
  const sheen = g.createLinearGradient(0, hgt / 2 - d * 0.48, 0, hgt / 2 + d * 0.2);
  sheen.addColorStop(0, 'rgba(255,255,255,0.3)');
  sheen.addColorStop(0.5, 'rgba(255,255,255,0.04)');
  sheen.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = sheen;
  g.fillRect(0, 0, w, hgt);
  g.restore();
  g.strokeStyle = 'rgba(255,255,255,0.22)';
  g.lineWidth = Math.max(1, d * 0.008);
  g.beginPath();
  g.arc(w / 2, hgt / 2, d * 0.48, 0, Math.PI * 2);
  g.stroke();
  return c;
}

const cache = new Map<WheelArtId, WheelArt>();

export function wheelArt(id: WheelArtId): WheelArt {
  let art = cache.get(id);
  if (!art) {
    const { colour, relief } = clawWheelCanvases(512);
    const map = new THREE.CanvasTexture(colour);
    map.colorSpace = THREE.SRGBColorSpace;
    map.anisotropy = 4;
    const bump = new THREE.CanvasTexture(relief);
    bump.colorSpace = THREE.NoColorSpace;
    art = { map, bump };
    cache.set(id, art);
  }
  return art;
}

/**
 * Give every wheel's tyre and rim the wheel-FACE projection: (u, v) = the
 * vertex's position in the wheel's own plane, with the hub at the centre and
 * the tyre's edge at the rim of the painting. Wheels are found the way
 * CarView.spinifyWheels finds them (Tyre_<id> / Rim_<id> / Trim_barrel_<id>,
 * plus the brake disc that shows between the spokes); the tyre gives the hub
 * and the radius. Written once per geometry.
 */
/**
 * M154: the corner a wheel part belongs to. "FL_lid" is the painted-wheel
 * lid (a disc behind the spokes, shown only under an art wheel set) and
 * belongs to corner "FL": it must spin with it and wear its face projection.
 */
export function wheelCorner(suffix: string): string {
  return suffix.replace(/_(lid|face)$/, '');
}

/** M154: the lid behind the spokes that only an art wheel set shows. */
export function isWheelLid(name: string): boolean {
  return /^Trim_barrel_.+_lid$/.test(name);
}

export function projectWheelFaces(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert();
  // M153: the mesh names are the NODE names here because ModelLoader hoists
  // them (gltfpack hangs each quantized mesh on an unnamed child node).
  const groups = new Map<string, { tyres: THREE.Mesh[]; meshes: THREE.Mesh[] }>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    // (M154: the muscle car's polished lip, Chrome_rimlip_<corner>, is part of
    // the face an art set paints)
    const match = /^(Tyre|Rim|Trim_barrel|Chrome_disc|Chrome_rimlip)_(.+)$/.exec(m.name);
    if (!match) return;
    const key = wheelCorner(match[2]);
    let e = groups.get(key);
    if (!e) groups.set(key, (e = { tyres: [], meshes: [] }));
    e.meshes.push(m);
    if (match[1] === 'Tyre') e.tyres.push(m);
  });
  const box = new THREE.Box3();
  const p = new THREE.Vector3();
  for (const [corner, g] of groups) {
    // A corner with no tyre of its own (the bike's second front disc,
    // Chrome_disc_F2) borrows the wheel it sits on: the corner its id extends.
    const tyres = g.tyres.length ? g.tyres : (groups.get(corner.replace(/\d+$/, ''))?.tyres ?? []);
    const { meshes } = g;
    if (!tyres.length) continue;
    // The tyre's box in root space (the union over a tyre built from several
    // parts, e.g. a painted bead ring): its centre is the hub; its thin
    // horizontal axis is the axle.
    box.makeEmpty();
    for (const tyre of tyres) {
      const rel = new THREE.Matrix4().copy(inv).multiply(tyre.matrixWorld);
      const tp = (tyre.geometry as THREE.BufferGeometry).attributes.position;
      for (let i = 0; i < tp.count; i++) box.expandByPoint(p.fromBufferAttribute(tp, i).applyMatrix4(rel));
    }
    const c = box.getCenter(new THREE.Vector3());
    const s = box.getSize(new THREE.Vector3());
    const along: 'x' | 'z' = s.x < s.z ? 'z' : 'x'; // the wheel's plane axis
    const radius = Math.max(s.y, s[along]) / 2 || 1;
    for (const m of meshes) {
      const geo = m.geometry as THREE.BufferGeometry;
      if (geo.userData.wheelUv || !geo.attributes.position) continue;
      const mr = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
      const pos = geo.attributes.position;
      const uv = new Float32Array(pos.count * 2);
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mr);
        uv[i * 2] = 0.5 + ((p[along] - c[along]) / radius) * 0.48;
        uv[i * 2 + 1] = 0.5 + ((p.y - c.y) / radius) * 0.48;
      }
      geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      geo.userData.wheelUv = true;
    }
  }
}

// ---------------------------------------------------------------------------
// M154: the art wheel's FACE.
// ---------------------------------------------------------------------------

/** A wheel measured from its tyre, in the root's frame. */
export interface WheelFrame {
  /** The hub (the tyre's box centre). */
  c: THREE.Vector3;
  /** The axle's axis and the wheel plane's horizontal axis. */
  axle: 'x' | 'z';
  along: 'x' | 'z';
  /** The tyre's outer radius. */
  radius: number;
  /** Where the tyre's beads sit along the axle (relative to the hub), the
   *  outer and inner side, and the bead radius on each. */
  tOut: number;
  tIn: number;
  rhoOut: number;
  rhoIn: number;
}

/**
 * Measure a wheel from its tyre's vertices (car-local or root-local points):
 * the hub, the axle, and the BEADS — the tyre's innermost ring on each face,
 * where it meets the rim. An art wheel's face disc closes the wheel there. Pure
 * (points in, numbers out); null for an empty or degenerate tyre.
 */
export function measureWheel(points: readonly THREE.Vector3[]): WheelFrame | null {
  if (points.length < 8) return null;
  const box = new THREE.Box3().setFromPoints(points as THREE.Vector3[]);
  const c = box.getCenter(new THREE.Vector3());
  const sz = box.getSize(new THREE.Vector3());
  const axle: 'x' | 'z' = sz.x < sz.z ? 'x' : 'z';
  const along: 'x' | 'z' = axle === 'x' ? 'z' : 'x';
  const radius = Math.max(sz.y, sz[along]) / 2;
  if (!(radius > 1e-4)) return null;
  const tr = points.map((p) => [p[axle] - c[axle], Math.hypot(p.y - c.y, p[along] - c[along])] as const);
  let lo = Infinity;
  let hi = -Infinity;
  for (const [, r] of tr) {
    lo = Math.min(lo, r);
    hi = Math.max(hi, r);
  }
  // the bead region: the tyre's inner quarter (bead seats, the return path)
  const inner = tr.filter(([, r]) => r <= lo + 0.25 * (hi - lo));
  let tOut = -Infinity;
  let tIn = Infinity;
  for (const [t] of inner) {
    tOut = Math.max(tOut, t);
    tIn = Math.min(tIn, t);
  }
  const near = (t0: number): number => {
    let r = lo;
    for (const [t, rr] of inner) if (Math.abs(t - t0) <= 0.012 && rr > r) r = rr;
    return r;
  };
  return { c, axle, along, radius, tOut, tIn, rhoOut: near(tOut), rhoIn: near(tIn) };
}

/** The face disc of one side (+1 = toward +axle) in the root frame: a fan of
 *  `segs` triangles at the bead plane, 2 mm inside it, reaching 3 mm past the
 *  bead (behind the sidewall), facing out of the wheel. */
export function wheelFacePoints(f: WheelFrame, side: 1 | -1, segs = 48): THREE.Vector3[] {
  const t = (side > 0 ? f.tOut : f.tIn) - side * 0.002;
  const r = (side > 0 ? f.rhoOut : f.rhoIn) + 0.003;
  const at = (a: number, rr: number): THREE.Vector3 => {
    const p = f.c.clone();
    p[f.axle] += t;
    p.y += Math.cos(a) * rr;
    p[f.along] += Math.sin(a) * rr;
    return p;
  };
  const centre = at(0, 0);
  const out: THREE.Vector3[] = [];
  // (y, along) x (y, along+) points along +x for axle x / along z, and along
  // -z for axle z / along x: wind so the face's normal leaves the wheel
  const sign = (f.axle === 'x' ? 1 : -1) * side;
  for (let i = 0; i < segs; i++) {
    const a0 = (i / segs) * Math.PI * 2;
    const a1 = ((i + 1) / segs) * Math.PI * 2;
    out.push(centre.clone(), ...(sign > 0 ? [at(a0, r), at(a1, r)] : [at(a1, r), at(a0, r)]));
  }
  return out;
}

const FACE_NAME = (corner: string): string => `Trim_barrel_${corner}_face`;

/**
 * M154: dress (or undress) a model's wheels for an ART wheel set (the Dragon
 * Claws). An art set paints one picture across the whole wheel; the stock rim
 * (one joined part: the lip ring, the spokes, the hub and its nuts) stood in
 * front of it as ghost spokes with the lip ring across the talons, and the
 * painted rim accent rings (the Formula's and the bike's lip rings, the
 * monster truck's beadlock rings) cut across them too. Under an art set those
 * hide and every wheel gets a FACE disc at its tyre's bead (measured, the
 * outer face, and both faces of a centred bike wheel), so the picture sits on
 * the face plus the tyre with no gap between them (the M154 lid stops at the
 * barrel, 4-5 cm inside the bead). Any other look shows the rim and rings
 * and hides the faces. The face geometry is made once per tyre geometry and
 * shared by every clone; its UVs come from projectWheelFaces with the tyre's.
 */
export function fitArtWheels(root: THREE.Object3D, art: boolean): void {
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert();
  const corners = new Map<string, { tyres: THREE.Mesh[]; rims: THREE.Mesh[]; faces: THREE.Mesh[] }>();
  const rings: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    if (/^(Lipring|Paint_bead_)/i.test(m.name)) {
      rings.push(m);
      return;
    }
    const match = /^(Tyre|Rim|Trim_barrel)_(.+)$/.exec(m.name);
    if (!match) return;
    const key = wheelCorner(match[2]);
    let e = corners.get(key);
    if (!e) corners.set(key, (e = { tyres: [], rims: [], faces: [] }));
    if (match[1] === 'Tyre') e.tyres.push(m);
    else if (match[1] === 'Rim') e.rims.push(m);
    else if (/_face$/.test(match[2])) e.faces.push(m);
  });
  for (const r of rings) r.visible = !art;
  for (const [corner, e] of corners) {
    for (const r of e.rims) r.visible = !art;
    for (const f of e.faces) f.visible = art;
    if (!art || e.faces.length || !e.tyres.length) continue;
    const tyre = e.tyres[0]!;
    const geo = tyre.geometry as THREE.BufferGeometry;
    let cached = geo.userData.artFaces as THREE.BufferGeometry[] | undefined;
    if (!cached) {
      // measured in the root's frame (the axle is a root axis), built in the
      // tyre's own frame so every clone of the model shares the geometry
      const toRoot = new THREE.Matrix4().copy(inv).multiply(tyre.matrixWorld);
      const pos = geo.attributes.position;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i < pos.count; i++) pts.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(toRoot));
      const f = measureWheel(pts);
      cached = [];
      if (f) {
        const toLocal = toRoot.clone().invert();
        // a wheel standing off the centreline shows its outer face; a
        // centred one (the bike's) is seen from both sides
        const tw = (f.tOut - f.tIn) / 2;
        const sides: (1 | -1)[] = Math.abs(f.c[f.axle]) > tw ? [f.c[f.axle] > 0 ? 1 : -1] : [1, -1];
        for (const side of sides) {
          const fp = wheelFacePoints(f, side).map((p) => p.clone().applyMatrix4(toLocal));
          const g = new THREE.BufferGeometry().setFromPoints(fp);
          g.computeVertexNormals();
          cached.push(g);
        }
      }
      geo.userData.artFaces = cached;
    }
    for (const g of cached) {
      const face = new THREE.Mesh(g, tyre.material);
      face.name = FACE_NAME(corner);
      face.castShadow = false;
      tyre.add(face);
    }
  }
}

// ---------------------------------------------------------------------------
// M154 r2: nothing stands in front of an art wheel's face.
// ---------------------------------------------------------------------------

/** The space in front of one face of an art wheel, in the root's frame: the
 *  wheel's disc (out to the tyre's tread) from the face's plane outward. */
export interface FaceFront {
  c: THREE.Vector3;
  axle: 'x' | 'z';
  along: 'x' | 'z';
  /** +1 / -1: which way along the axle the face looks. */
  side: 1 | -1;
  /** The face's plane and how far out the space reaches (along the axle,
   *  measured from the hub toward `side`). */
  t0: number;
  t1: number;
  r: number;
}

/** How far out from a face the space in front of it is kept clear (a bike's
 *  fork leg, caliper and axle end stand about this far out). */
const FRONT_DEPTH = 0.3;

/** The spaces in front of a measured wheel's faces (one side, or both on a
 *  centred wheel). Pure. */
export function faceFronts(f: WheelFrame, sides: readonly (1 | -1)[]): FaceFront[] {
  return sides.map((side) => {
    const t0 = side > 0 ? f.tOut : -f.tIn;
    return { c: f.c.clone(), axle: f.axle, along: f.along, side, t0, t1: t0 + FRONT_DEPTH, r: f.radius };
  });
}

/** True when a root-frame point stands in front of the face (inside the
 *  wheel's disc, outboard of the face's plane). Pure. */
export function inFaceFront(p: THREE.Vector3, F: FaceFront): boolean {
  const t = (p[F.axle] - F.c[F.axle]) * F.side;
  if (t <= F.t0 + 0.001 || t >= F.t1) return false;
  return Math.hypot(p.y - F.c.y, p[F.along] - F.c[F.along]) < F.r;
}

/** The mechanical parts that stand aside for an art face (exported for the
 *  tests): brake hardware, forks, axles and their clamps, the chain, the
 *  sprocket and the swingarm, never bodywork or a cavity's lining.
 *  (Charcoal_ is a satin panel or lining finish, never wheel hardware: the
 *  bike's fairing vent backs sit over the front wheel and were cut into
 *  see-through holes.) */
export function faceFrontPart(m: THREE.Mesh): boolean {
  const mat = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material | undefined;
  if (/well/i.test(`${m.name} ${mat?.name ?? ''}`)) return false;
  return ['chrome', 'gold', 'trim', 'brake', 'carbon', 'rim'].includes(carPartOf(m));
}

const CUT_FRAGMENT = `
for (int i = 0; i < 4; i++) {
  if (i >= uCutN) break;
  vec3 d = vCutP - uCutC[i].xyz;
  float t = (uCutA[i].x > 0.5 ? d.x : d.z) * uCutA[i].y;
  float r = uCutA[i].x > 0.5 ? length(d.yz) : length(d.xy);
  if (r < uCutC[i].w && t > uCutA[i].z && t < uCutA[i].w) discard;
}`;

/**
 * M154 r2: under an art wheel set, nothing mechanical stands in front of a
 * wheel's painted face. On the motorbike its front brake (two rotors on
 * carriers, the calipers), the axle's ends and clamps and the fork's lower
 * legs, and at the back the sprocket, the chain and the swingarm, all stand
 * outboard of the face (a bike's wheel is narrower than its fork): the cars'
 * calipers hide behind the face. A part wholly in front of a face hides; a
 * part reaching into that space (a fork leg, the swingarm) is CUT there, so
 * outside the wheel's disc it stays drawn and the wheel reads held by it,
 * and inside it reads as passing behind the wheel. M154 polish r3: back to
 * this, the live rule: round 1 kept the fork, the axle clamp, the sprocket
 * and the chain drawn ACROSS the bone face (a gold leg, a black stub and a
 * toothed ring on the paid art, the fleet review). Bodywork and satin panels
 * (the fairing's vents) are never touched. Any other look restores
 * everything. Call AFTER the materials are dressed (the cut rides on the
 * mesh's own material, cloned per mesh).
 */
export function clearArtWheelFronts(root: THREE.Object3D, art: boolean): void {
  const prev = (root.userData.artCut ?? []) as ArtCut[];
  for (const { mesh, mat, orig } of prev) {
    // (a re-dress has already given the mesh its new material: keep that)
    if (mesh.material === mat) mesh.material = orig;
    mat.dispose();
  }
  root.userData.artCut = [];
  const hidden: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o.userData.artFrontHidden) hidden.push(o);
  });
  for (const o of hidden) {
    o.visible = true;
    delete o.userData.artFrontHidden;
  }
  if (!art) return;
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert();
  const fronts: FaceFront[] = [];
  const meshes: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.visible) return;
    if (/^Tyre_/.test(m.name)) {
      const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
      const pos = (m.geometry as THREE.BufferGeometry).attributes.position;
      if (!pos) return;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i < pos.count; i++) pts.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(rel));
      const f = measureWheel(pts);
      if (!f) return;
      const tw = (f.tOut - f.tIn) / 2;
      const sides: (1 | -1)[] = Math.abs(f.c[f.axle]) > tw ? [f.c[f.axle] > 0 ? 1 : -1] : [1, -1];
      fronts.push(...faceFronts(f, sides));
    } else if (!/_(face|lid)$/.test(m.name)) meshes.push(m);
  });
  if (!fronts.length) return;
  const cuts = root.userData.artCut as ArtCut[];
  const p = new THREE.Vector3();
  for (const m of meshes) {
    if (!faceFrontPart(m)) continue;
    const rel = new THREE.Matrix4().copy(inv).multiply(m.matrixWorld);
    const pos = (m.geometry as THREE.BufferGeometry).attributes.position;
    if (!pos) continue;
    let inside = 0;
    const hit = new Set<number>();
    for (let i = 0; i < pos.count; i++) {
      p.fromBufferAttribute(pos, i).applyMatrix4(rel);
      const k = fronts.findIndex((F) => inFaceFront(p, F));
      if (k >= 0) {
        inside++;
        hit.add(k);
      }
    }
    if (!inside) continue;
    if (inside === pos.count) {
      m.visible = false;
      m.userData.artFrontHidden = true;
      continue;
    }
    const src = (Array.isArray(m.material) ? m.material[0] : m.material) as THREE.Material;
    const regions = [...hit].slice(0, 4).map((k) => fronts[k]!);
    const mat = cutMaterial(src, regions, rel);
    cuts.push({ mesh: m, mat, orig: m.material });
    m.material = mat;
  }
}

/** A mesh cut in front of an art wheel's face: its cut material and the one
 *  it wore before. */
interface ArtCut {
  mesh: THREE.Mesh;
  mat: THREE.Material;
  orig: THREE.Material | THREE.Material[];
}

/** A copy of `src` that discards what lies in the given spaces (mesh-local
 *  positions taken to the root's frame by `toRoot`). */
function cutMaterial(src: THREE.Material, regions: readonly FaceFront[], toRoot: THREE.Matrix4): THREE.Material {
  const mat = src.clone();
  const c = regions.map((F) => new THREE.Vector4(F.c.x, F.c.y, F.c.z, F.r));
  const a = regions.map((F) => new THREE.Vector4(F.axle === 'x' ? 1 : 0, F.side, F.t0 + 0.001, F.t1));
  while (c.length < 4) {
    c.push(new THREE.Vector4());
    a.push(new THREE.Vector4());
  }
  const uniforms = {
    uCutM: { value: toRoot.clone() },
    uCutC: { value: c },
    uCutA: { value: a },
    uCutN: { value: regions.length },
  };
  const before = src.onBeforeCompile.bind(src);
  const key = src.customProgramCacheKey.bind(src);
  mat.onBeforeCompile = (shader, renderer): void => {
    before(shader, renderer);
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform mat4 uCutM;\nvarying vec3 vCutP;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvCutP = (uCutM * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec4 uCutC[4];\nuniform vec4 uCutA[4];\nuniform int uCutN;\nvarying vec3 vCutP;')
      .replace('#include <clipping_planes_fragment>', `#include <clipping_planes_fragment>\n${CUT_FRAGMENT}`);
  };
  mat.customProgramCacheKey = (): string => `${key()}|dr-artcut`;
  return mat;
}
